import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  MenuItem,
  useTheme,
  IconButton,
  Divider,
  alpha,
} from '@mui/material';
import {
  Send as SendIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import SEO from '../../components/SEO';
import { useNotification } from '../../contexts/NotificationContext';

const Contact: React.FC = () => {
  const theme = useTheme();
  const { showSuccess } = useNotification();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'Question',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate sending
    setTimeout(() => {
      showSuccess(`Your ${formData.subject.toLowerCase()} has been sent! We will get back to you soon.`);
      setFormData({ name: '', email: '', subject: 'Question', message: '' });
    }, 500);
  };

  return (
    <Box>
      <SEO 
        title="Contact Us" 
        description="Get in touch with us for questions, comments, or to make an appointment. View our store location and operating hours."
      />
      
      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: 8, 
          pb: 6, 
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white',
          textAlign: 'center',
          borderRadius: { xs: 0, sm: 2 },
          mb: 6,
          boxShadow: theme.shadows[4]
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Get in Touch
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
            Whether you have a question, need an appraisal, or want to book a VIP appointment, our team is here to help.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={6}>
          
          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Card elevation={3} sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Send us a Message
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Fill out the form below and we'll get back to you as soon as possible.
                </Typography>
                
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        required
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        variant="outlined"
                      >
                        <MenuItem value="Question">General Question</MenuItem>
                        <MenuItem value="Appointment">Make an Appointment</MenuItem>
                        <MenuItem value="Appraisal">Card Appraisal Request</MenuItem>
                        <MenuItem value="Feedback">Feedback / Comments</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        multiline
                        rows={6}
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="How can we help you?"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        endIcon={<SendIcon />}
                        sx={{ 
                          py: 1.5, 
                          px: 4, 
                          borderRadius: 2,
                          fontWeight: 'bold'
                        }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* Store Info & Location */}
          <Grid item xs={12} md={5}>
            <Card elevation={3} sx={{ borderRadius: 3, height: '100%', background: theme.palette.background.paper }}>
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Store Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Visit our physical location to browse our premium collection in person.
                </Typography>

                <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}>
                  <LocationOnIcon color="primary" sx={{ mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Address</Typography>
                    <Typography variant="body2" color="text.secondary">
                      1234 TCG Avenue<br />
                      Suite 100<br />
                      Card City, CA 90210
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Phone</Typography>
                    <Typography variant="body2" color="text.secondary">(555) 123-4567</Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                  <EmailIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
                    <Typography variant="body2" color="text.secondary">hello@chasingtcg.com</Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start' }}>
                  <AccessTimeIcon color="primary" sx={{ mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Operating Hours</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Mon - Fri: 11:00 AM - 8:00 PM<br />
                      Saturday: 10:00 AM - 9:00 PM<br />
                      Sunday: 12:00 PM - 6:00 PM
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Connect With Us
                  </Typography>
                  <Box display="flex" gap={1}>
                    <IconButton color="primary" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <InstagramIcon />
                    </IconButton>
                    <IconButton color="primary" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <FacebookIcon />
                    </IconButton>
                    <IconButton color="primary" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <TwitterIcon />
                    </IconButton>
                  </Box>
                </Box>

              </CardContent>
            </Card>
          </Grid>
          
          {/* Map Section */}
          <Grid item xs={12}>
            <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box 
                sx={{ 
                  height: 400, 
                  width: '100%', 
                  bgcolor: 'grey.300',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* Fallback to embedded Google Maps iframe placeholder */}
                <iframe
                  title="Store Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3305.7152203584424!2d-118.40224108478442!3d34.05118798060614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2bc04d3d147ab%3A0xd6c7c379fd081ed1!2sSanta%20Monica%20Pier!5e0!3m2!1sen!2sus!4v1680123456789!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={false}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </Box>
            </Card>
          </Grid>
          
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
